import { askConfirm } from '@/components/ui/ConfirmHost';
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getClasses, getSubjects, getStaff, getTimetable,
  postTimetableEntry, putTimetableEntry, deleteTimetableEntry,
} from '@/api/owner';
import { Clock, Plus, X, Trash2, Edit2 } from 'lucide-react';

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  return [];
};

const DAYS = [
  { n: 1, name: 'Monday' },
  { n: 2, name: 'Tuesday' },
  { n: 3, name: 'Wednesday' },
  { n: 4, name: 'Thursday' },
  { n: 5, name: 'Friday' },
  { n: 6, name: 'Saturday' },
];

type ModalState =
  | { kind: 'new'; weekday: number; period: number }
  | { kind: 'edit'; entry: any }
  | null;

export function TimetablePage() {
  const qc = useQueryClient();
  const [classId, setClassId] = useState<number | ''>('');
  const [teacherId, setTeacherId] = useState<number | ''>('');
  const [modal, setModal] = useState<ModalState>(null);

  const classesQ = useQuery({ queryKey: ['owner-classes'], queryFn: () => getClasses().then((r: any) => r.data?.data ?? r.data) });
  const subjectsQ = useQuery({ queryKey: ['owner-subjects'], queryFn: () => getSubjects().then((r: any) => r.data?.data ?? r.data) });
  const staffQ = useQuery({ queryKey: ['owner-staff'], queryFn: () => getStaff().then((r: any) => r.data?.data ?? r.data) });
  const classes = arr<any>(classesQ.data);
  const subjects = arr<any>(subjectsQ.data);
  const staff = arr<any>(staffQ.data).filter((s: any) => ['teacher', 'desk', 'owner'].includes(s.role));

  useEffect(() => {
    if (!classId && classes.length > 0) setClassId(classes[0].id);
  }, [classes, classId]);

  const timetableQ = useQuery({
    queryKey: ['owner-timetable', classId, teacherId],
    queryFn: () =>
      getTimetable({
        class_id: classId || undefined,
        teacher_id: teacherId || undefined,
      }).then((r: any) => r.data?.data ?? r.data),
    enabled: !!classId || !!teacherId,
    retry: false,
  });
  const entries = arr<any>(timetableQ.data);

  // Build lookup: `{weekday}_{period}` → entry
  const lookup = useMemo(() => {
    const m: Record<string, any> = {};
    for (const e of entries) {
      if (e.weekday && e.period) m[`${e.weekday}_${e.period}`] = e;
    }
    return m;
  }, [entries]);

  // Derive max period from data (default 8)
  const maxPeriod = useMemo(() => {
    const top = entries.reduce((m: number, e: any) => Math.max(m, Number(e.period) || 0), 0);
    return Math.max(8, top);
  }, [entries]);

  const refresh = () => qc.invalidateQueries({ queryKey: ['owner-timetable'] });

  const deleteM = useMutation({
    mutationFn: (id: number) => deleteTimetableEntry(id),
    onSuccess: () => { setModal(null); refresh(); },
    onError: (e: any) => alert(e.response?.data?.message ?? 'Delete failed'),
  });

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto p-4 sm:p-6 pb-10">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">ACADEMICS · TIMETABLE</p>
          <h1 className="text-3xl font-extrabold text-slate-900">Timetable</h1>
          <p className="text-slate-500 text-sm mt-1">Weekly class schedule — click any cell to add or edit</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select value={classId} onChange={(e) => setClassId(Number(e.target.value) || '')} className="border border-slate-300 rounded-lg px-3 py-2 text-sm font-semibold bg-white">
            <option value="">All classes</option>
            {classes.map((c: any) => <option key={c.id} value={c.id}>{c.name}{c.arm ? ` ${c.arm}` : ''}</option>)}
          </select>
          <select value={teacherId} onChange={(e) => setTeacherId(Number(e.target.value) || '')} className="border border-slate-300 rounded-lg px-3 py-2 text-sm font-semibold bg-white">
            <option value="">All teachers</option>
            {staff.map((s: any) => <option key={s.id} value={s.id}>{s.name ?? `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim()}</option>)}
          </select>
        </div>
      </div>

      {!classId && !teacherId ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500">
          Select a class to view the timetable
        </div>
      ) : timetableQ.isLoading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500">Loading…</div>
      ) : timetableQ.isError ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-800">
          <b>Could not load timetable.</b>{' '}
          {(timetableQ.error as any)?.response?.data?.message ?? 'Check your connection.'}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider border-b border-slate-200">
              <tr>
                <th className="px-3 py-3 w-16 text-center">Period</th>
                {DAYS.map((d) => <th key={d.n} className="px-2 py-3 text-center">{d.name}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {Array.from({ length: maxPeriod }, (_, i) => i + 1).map((p) => (
                <tr key={p}>
                  <td className="px-3 py-3 font-bold text-slate-700 text-center">{p}</td>
                  {DAYS.map((d) => {
                    const cell = lookup[`${d.n}_${p}`];
                    return (
                      <td key={d.n} className="px-1 py-1 align-top">
                        {cell ? (
                          <button
                            onClick={() => setModal({ kind: 'edit', entry: cell })}
                            className="w-full text-left bg-blue-50 border border-blue-200 rounded p-2 hover:border-blue-400 transition"
                          >
                            <div className="font-bold text-blue-900 text-xs truncate">{cell.subject ?? '—'}</div>
                            {cell.teacher && <div className="text-[10px] text-blue-700 truncate">{cell.teacher}</div>}
                            {cell.starts_at && <div className="text-[10px] text-slate-500 mt-0.5">{cell.starts_at}–{cell.ends_at}</div>}
                            {cell.room && <div className="text-[10px] text-slate-500">{cell.room}</div>}
                          </button>
                        ) : (
                          <button
                            onClick={() => setModal({ kind: 'new', weekday: d.n, period: p })}
                            className="w-full h-14 bg-slate-50 rounded border border-dashed border-slate-200 hover:bg-blue-50 hover:border-blue-300 transition flex items-center justify-center text-slate-300 hover:text-blue-500"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <EntryModal
          mode={modal.kind}
          data={modal.kind === 'edit' ? modal.entry : { weekday: modal.weekday, period: modal.period, school_class_id: classId }}
          classes={classes}
          subjects={subjects}
          teachers={staff}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); refresh(); }}
          onDelete={async (id) => { if (await askConfirm('Remove this entry from the timetable?')) deleteM.mutate(id); }}
        />
      )}

      <div className="text-xs text-slate-500 bg-slate-50 rounded-xl p-3 border border-slate-200 flex items-start gap-2">
        <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>
          Times use 24-hour format (HH:MM). The backend blocks clashes — a class can't have two
          lessons in the same period, and a teacher can't be in two classes at once.
        </span>
      </div>
    </div>
  );
}

function EntryModal({
  mode, data, classes, subjects, teachers, onClose, onSaved, onDelete,
}: {
  mode: 'new' | 'edit';
  data: any;
  classes: any[];
  subjects: any[];
  teachers: any[];
  onClose: () => void;
  onSaved: () => void;
  onDelete: (id: number) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    const fd = new FormData(e.currentTarget);
    const body: any = {
      school_class_id: Number(fd.get('school_class_id')),
      subject_id: fd.get('subject_id') ? Number(fd.get('subject_id')) : null,
      teacher_id: fd.get('teacher_id') ? Number(fd.get('teacher_id')) : null,
      weekday: Number(fd.get('weekday')),
      period: Number(fd.get('period')),
      starts_at: String(fd.get('starts_at')),
      ends_at: String(fd.get('ends_at')),
      room: String(fd.get('room') ?? '') || null,
      kind: String(fd.get('kind') ?? 'lesson'),
    };
    try {
      if (mode === 'new') await postTimetableEntry(body);
      else await putTimetableEntry(data.id, body);
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <h3 className="font-bold text-slate-900">
            {mode === 'new' ? 'Add to timetable' : 'Edit entry'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={submit} className="p-6 overflow-y-auto space-y-3">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Class</label>
            <select name="school_class_id" required defaultValue={data.school_class_id ?? ''} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm">
              <option value="">Choose…</option>
              {classes.map((c: any) => <option key={c.id} value={c.id}>{c.name}{c.arm ? ` ${c.arm}` : ''}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Subject</label>
            <select name="subject_id" defaultValue={data.subject_id ?? ''} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm">
              <option value="">— None —</option>
              {subjects.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Teacher</label>
            <select name="teacher_id" defaultValue={data.teacher_id ?? ''} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm">
              <option value="">— None —</option>
              {teachers.map((t: any) => <option key={t.id} value={t.id}>{t.name ?? `${t.first_name ?? ''} ${t.last_name ?? ''}`.trim()}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Day</label>
              <select name="weekday" required defaultValue={data.weekday ?? 1} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm">
                {DAYS.map((d) => <option key={d.n} value={d.n}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Period</label>
              <input name="period" type="number" min={1} max={15} required defaultValue={data.period ?? 1} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Starts</label>
              <input name="starts_at" type="time" required defaultValue={data.starts_at ?? '08:00'} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Ends</label>
              <input name="ends_at" type="time" required defaultValue={data.ends_at ?? '08:45'} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Room</label>
              <input name="room" defaultValue={data.room ?? ''} placeholder="Lab 2" className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Kind</label>
              <select name="kind" defaultValue={data.kind ?? 'lesson'} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm">
                <option value="lesson">Lesson</option>
                <option value="break">Break</option>
                <option value="assembly">Assembly</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex gap-2 pt-3">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-slate-300 rounded-lg font-semibold text-sm">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
              {saving ? 'Saving…' : mode === 'new' ? 'Add entry' : 'Save changes'}
            </button>
          </div>

          {mode === 'edit' && (
            <button
              type="button"
              onClick={() => onDelete(data.id)}
              className="w-full mt-1 py-2 border border-red-200 bg-red-50 text-red-700 rounded-lg font-semibold text-sm flex items-center justify-center gap-1"
            >
              <Trash2 className="w-4 h-4" /> Remove entry
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
